"use client";

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  TextFieldSts,
  Dropdown,
  DropdownItem,
  InlineInfoBox,
} from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import InputValidationWrapper from "@/components/atoms/input-validation-wrapper/input-validation-wrapper";
import Styles from "@/components/molecules/questions-container/organizer/organiser-answer/organiser-form.module.css";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { smoothScrollToRef } from "@/utils/scrollUtils";

import TravellerSection from "./traveller-section";
import { useTravellersRequiredInformation } from "../../provider/travellers-required-information.provider";
import {
  isIdentificationOptional,
  getErrorDetails,
} from "../../utils/validation.utils";

const DROPDOWN_LIST_BOX_STYLE = {
  outline: "none",
  margin: "0",
  minWidth: "unset !important",
  boxShadow: "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
};

export default function FormSection() {
  const { current } = useWizard();
  const { t } = useWizardTranslation();
  const {
    magnoliaData,
    identificationTypes,
    organiserData,
    travellersData,
    organiserValidation,
    travellersValidation,
    organiserErrors,
    travellersErrors,
    updateOrganiserField,
    updateTravellerField,
    setFieldTouched,
    showErrors,
  } = useTravellersRequiredInformation();

  // Get field length constants from wizard definition
  const MAX_ADDRESS_LENGTH =
    current?.brandSpecificParams?.maxAddressLength ?? 60;
  const MAX_CITY_LENGTH = current?.brandSpecificParams?.maxCityLength ?? 40;
  const MAX_POSTAL_CODE_LENGTH =
    current?.brandSpecificParams?.maxPostalCodeLength ?? 5;

  // Refs for scrolling to first error
  const firstErrorRef = useRef<HTMLDivElement | null>(null);
  const organiserAddressLine1Ref = useRef<HTMLDivElement | null>(null);
  const organiserAddressLine2Ref = useRef<HTMLButtonElement | null>(null);
  const organiserCityRef = useRef<HTMLButtonElement | null>(null);
  const organiserPostalCodeRef = useRef<HTMLButtonElement | null>(null);
  const organiserIdentificationTypeRef = useRef<HTMLDivElement | null>(null);
  const organiserIdentificationNumberRef = useRef<HTMLButtonElement | null>(
    null
  );

  // Helper function to filter identification types based on passport data (derived state)
  const getFilteredIdentificationTypes = useCallback(
    (
      identificationType: string | undefined,
      identificationNumber: string | undefined
    ) => {
      return identificationTypes.filter((type) => {
        // Show PASSPORT option only if there's saved passport data
        if (type.id === "PASSPORT") {
          return identificationType === "PASSPORT" && !!identificationNumber;
        }
        // Show all other identification types
        return true;
      });
    },
    [identificationTypes]
  );

  // Track component mount to prevent scroll on initial render
  const hasMounted = useRef(false);

  useEffect(() => {
    hasMounted.current = true;
  }, []);

  // Create refs for traveller fields dynamically
  const travellerRefs = useMemo(() => {
    const refs: Record<
      string,
      {
        identificationTypeRef: React.RefObject<HTMLDivElement | null>;
        identificationNumberRef: React.RefObject<HTMLButtonElement | null>;
      }
    > = {};

    travellersData.forEach((traveller) => {
      refs[traveller.encryptedId] = {
        identificationTypeRef: React.createRef<HTMLDivElement>(),
        identificationNumberRef: React.createRef<HTMLButtonElement>(),
      };
    });

    return refs;
  }, [travellersData]);

  // Calculate error details
  const errorDetails = useMemo(
    () =>
      getErrorDetails({
        showErrors,
        organiserValidation,
        travellersValidation,
        travellersData,
      }),
    [showErrors, organiserValidation, travellersValidation, travellersData]
  );

  const content = magnoliaData;
  const hasTravellers = travellersData.length > 0;

  // Helper functions for error messages
  const getAlertMessageIncompleteSection = useCallback(() => {
    if (errorDetails.count === 1) {
      return (
        content?.errorMessages?.alertMessageIncompleteSectionSingle?.replace(
          "<ERROR_COUNT>",
          errorDetails.count.toString()
        ) || ""
      );
    }
    return (
      content?.errorMessages?.alertMessageIncompleteSection?.replace(
        "<ERROR_COUNT>",
        errorDetails.count.toString()
      ) || ""
    );
  }, [content, errorDetails.count]);

  const getAlertMessageLinkSection = useCallback(() => {
    if (errorDetails.count === 1) {
      return content?.errorMessages?.alertMessageLinkSectionSingle || "";
    }
    return content?.errorMessages?.alertMessageLinkSection || "";
  }, [content, errorDetails.count]);

  const getOrganiserErrorMessage = useCallback(
    (
      fieldName: string,
      errorKey: string | null,
      validationStatus: "success" | "error" | "warning" | null
    ): string => {
      if (!validationStatus || validationStatus === "success") return "";

      if (validationStatus === "warning") {
        const warnings = content?.organiserSection?.addressSection?.warnings;

        const warningKeyMap: Record<
          string,
          "line1Warning" | "cityWarning" | "postalCodeWarning"
        > = {
          addressLine1: "line1Warning",
          city: "cityWarning",
          postalCode: "postalCodeWarning",
        };

        const warningKey = warningKeyMap[fieldName];
        return warnings?.[warningKey] ?? "";
      }

      if (!errorKey) return "";
      const errors = content?.organiserSection?.addressSection?.errors;
      return (
        errors?.[
          errorKey as
            | "line1InvalidChars"
            | "line2InvalidChars"
            | "cityInvalidFormat"
            | "postalCodeInvalidFormat"
        ] ?? ""
      );
    },
    [content]
  );

  const getIdentificationErrorMessage = useCallback(
    (
      errorKey: string | null,
      validationStatus: "success" | "error" | "warning" | null
    ): string => {
      if (!validationStatus || validationStatus === "success") return "";

      if (validationStatus === "warning") {
        const warnings =
          content?.organiserSection?.identificationSection?.warnings;
        return warnings?.numberWarning ?? "";
      }

      if (!errorKey) return "";
      const errors = content?.organiserSection?.identificationSection?.errors;
      return (
        errors?.[
          errorKey as
            | "identificationNumberInvalidFormat"
            | "typeRequiredForNumber"
        ] ?? ""
      );
    },
    [content]
  );

  const handleOrganiserIdentificationTypeChange = useCallback(
    (value: string) => {
      updateOrganiserField("identificationType", value);
      setFieldTouched("organiser", "identificationType");

      // Scroll to identification number field after selection
      if (hasMounted.current) {
        smoothScrollToRef(organiserIdentificationNumberRef, 100);
      }
    },
    [updateOrganiserField, setFieldTouched]
  );

  const handleOrganiserIdentificationTypeBlur = useCallback(() => {
    setFieldTouched("organiser", "identificationType");
  }, [setFieldTouched]);

  return (
    <div className="w-full flex justify-center">
      <div className="lg:w-[414px] md:w-[400px] w-full">
        <h1 className="font-header-font md:text-[39px] text-[26px] md:leading-[45px] leading-[30px] text-text-title md:text-left text-center mb-spacing-l">
          {content?.pageTitle ?? ""}
        </h1>

        {/* Organiser Name & Age (Read-only) */}
        <div className="mb-spacing-m">
          <p className="font-body-font text-xs text-text-body leading-27 font-semibold">
            {organiserData.firstName} {organiserData.lastName} (
            {t("organiser_label", "Organiser")}),{" "}
            <span className="font-semibold">{organiserData.age}</span>
          </p>
        </div>

        {/* Address Section */}
        <div className="mb-spacing-m">
          {/* Address Line 1 */}
          <div className="mb-spacing-m" ref={organiserAddressLine1Ref}>
            <InputValidationWrapper
              className="flex items-start"
              status={organiserValidation.addressLine1}
              childRef={
                errorDetails.firstError === "organiser-addressLine1"
                  ? firstErrorRef
                  : undefined
              }
            >
              <TextFieldSts
                label={
                  content?.organiserSection?.addressSection?.line1Placeholder ??
                  "Address Line 1"
                }
                data-testid="organiser-address-line1"
                id="organiser-address-line1"
                width="100%"
                name="addressLine1"
                maxLength={MAX_ADDRESS_LENGTH}
                msgCustomStyles={{ width: "auto" }}
                state={organiserValidation.addressLine1 ?? undefined}
                message={getOrganiserErrorMessage(
                  "addressLine1",
                  organiserErrors.addressLine1,
                  organiserValidation.addressLine1
                )}
                value={organiserData.addressLine1}
                onChange={(value) =>
                  updateOrganiserField("addressLine1", value)
                }
                onBlur={() => setFieldTouched("organiser", "addressLine1")}
                brand="staysure"
              />
            </InputValidationWrapper>
          </div>

          {/* Address Line 2 */}
          <div className="mb-spacing-m">
            <button
              type="button"
              tabIndex={-1}
              ref={organiserAddressLine2Ref}
              onFocus={() => {
                if (hasMounted.current) {
                  smoothScrollToRef(organiserAddressLine1Ref, 100);
                }
              }}
              data-testid="organiser-address-line2-scroll-id"
              className="w-full focus-visible:outline-none"
            >
              <InputValidationWrapper
                className="flex items-start"
                status={organiserValidation.addressLine2}
                childRef={
                  errorDetails.firstError === "organiser-addressLine2"
                    ? firstErrorRef
                    : undefined
                }
              >
                <TextFieldSts
                  label={
                    content?.organiserSection?.addressSection
                      ?.line2Placeholder ?? "Address Line 2 (Optional)"
                  }
                  data-testid="organiser-address-line2"
                  id="organiser-address-line2"
                  width="100%"
                  name="addressLine2"
                  maxLength={MAX_ADDRESS_LENGTH}
                  msgCustomStyles={{ width: "auto" }}
                  state={organiserValidation.addressLine2 ?? undefined}
                  message={getOrganiserErrorMessage(
                    "addressLine2",
                    organiserErrors.addressLine2,
                    organiserValidation.addressLine2
                  )}
                  value={organiserData.addressLine2 || ""}
                  onChange={(value) =>
                    updateOrganiserField("addressLine2", value)
                  }
                  onBlur={() => setFieldTouched("organiser", "addressLine2")}
                  brand="staysure"
                />
              </InputValidationWrapper>
            </button>
          </div>

          {/* City/Town */}
          <div className="mb-spacing-m">
            <button
              type="button"
              tabIndex={-1}
              ref={organiserCityRef}
              onFocus={() => {
                if (hasMounted.current) {
                  smoothScrollToRef(organiserAddressLine2Ref, 100);
                }
              }}
              data-testid="organiser-city-scroll-id"
              className="w-full focus-visible:outline-none"
            >
              <InputValidationWrapper
                className="flex items-start"
                status={organiserValidation.city}
                childRef={
                  errorDetails.firstError === "organiser-city"
                    ? firstErrorRef
                    : undefined
                }
              >
                <TextFieldSts
                  label={
                    content?.organiserSection?.addressSection
                      ?.cityPlaceholder ?? "City/Town"
                  }
                  data-testid="organiser-city"
                  id="organiser-city"
                  width="100%"
                  name="city"
                  maxLength={MAX_CITY_LENGTH}
                  msgCustomStyles={{ width: "auto" }}
                  state={organiserValidation.city ?? undefined}
                  message={getOrganiserErrorMessage(
                    "city",
                    organiserErrors.city,
                    organiserValidation.city
                  )}
                  value={organiserData.city}
                  onChange={(value) => updateOrganiserField("city", value)}
                  onBlur={() => setFieldTouched("organiser", "city")}
                  brand="staysure"
                />
              </InputValidationWrapper>
            </button>
          </div>

          {/* Postal Code */}
          <div className="mb-spacing-m">
            <button
              type="button"
              tabIndex={-1}
              ref={organiserPostalCodeRef}
              onFocus={() => {
                if (hasMounted.current) {
                  smoothScrollToRef(organiserCityRef, 100);
                }
              }}
              data-testid="organiser-postal-code-scroll-id"
              className="w-full focus-visible:outline-none"
            >
              <InputValidationWrapper
                className="flex items-start"
                status={organiserValidation.postalCode}
                childRef={
                  errorDetails.firstError === "organiser-postalCode"
                    ? firstErrorRef
                    : undefined
                }
              >
                <TextFieldSts
                  label={
                    content?.organiserSection?.addressSection
                      ?.postalCodePlaceholder ?? "Postal Code"
                  }
                  data-testid="organiser-postal-code"
                  id="organiser-postal-code"
                  width="100%"
                  name="postalCode"
                  maxLength={MAX_POSTAL_CODE_LENGTH}
                  msgCustomStyles={{ width: "auto" }}
                  state={organiserValidation.postalCode ?? undefined}
                  message={getOrganiserErrorMessage(
                    "postalCode",
                    organiserErrors.postalCode,
                    organiserValidation.postalCode
                  )}
                  value={organiserData.postalCode}
                  onChange={(value) =>
                    updateOrganiserField("postalCode", value)
                  }
                  onBlur={() => setFieldTouched("organiser", "postalCode")}
                  brand="staysure"
                />
              </InputValidationWrapper>
            </button>
          </div>
        </div>

        {/* Identification Section */}
        <div className={hasTravellers ? "mb-spacing-l" : ""}>
          {/* Identification Type Dropdown */}
          <InputValidationWrapper
            className="flex flex-row mb-spacing-m"
            status={organiserValidation.identificationType}
            childRef={
              errorDetails.firstError === "organiser-identificationType"
                ? firstErrorRef
                : undefined
            }
          >
            <div
              ref={organiserIdentificationTypeRef}
              className={`${Styles.country_wrapper} ${Styles.title_dropdown} flex flex-row items-start w-full h-[56px]`}
            >
              <Dropdown
                id="organiser-identification-type"
                defaultValue={organiserData.identificationType}
                placeholderTextAndLabel={
                  content?.organiserSection?.identificationSection
                    ?.typePlaceholder ?? "Identification Type"
                }
                strokeColor="#3B3B3B"
                onSelectionChange={handleOrganiserIdentificationTypeChange}
                onDropdownBlur={handleOrganiserIdentificationTypeBlur}
                className={`${Styles.droplistBox} ${
                  organiserValidation.identificationType === "warning"
                    ? Styles.warningMsgClass
                    : ""
                } ${
                  organiserValidation.identificationType === "success"
                    ? Styles.successMsgClass
                    : ""
                } border-interactive-secondary-default rounded-radius-lg max-h-spacing-xxxl !py-spacing-m !px-spacing-l`}
                placeholderLeftBeforeSelect={0}
                placeholderLeftAfterSelect={-24}
                listBoxStyle={DROPDOWN_LIST_BOX_STYLE}
                listClassName={`${Styles.listStyle} mt-[10px] rounded-radius-s`}
                unorderedListClassName="w-full !mt-[3px] !md:ml-0 !ml-[-1px] !border-border-subdued !rounded-radius-lg !px-spacing-m !py-spacing-xs"
              >
                {getFilteredIdentificationTypes(
                  organiserData.identificationType,
                  organiserData.identificationNumber
                ).map((type) => (
                  <DropdownItem key={type.id} value={type.id}>
                    <span className="w-full leading-[24px] font-proximanova text-2xs font-semibold p-spacing-xs">
                      {type.label}
                    </span>
                  </DropdownItem>
                ))}
              </Dropdown>
            </div>
          </InputValidationWrapper>

          {/* Identification Number */}
          <div className="mb-spacing-m">
            <button
              type="button"
              tabIndex={-1}
              ref={organiserIdentificationNumberRef}
              onFocus={() => {
                if (hasMounted.current) {
                  smoothScrollToRef(organiserPostalCodeRef, 100);
                }
              }}
              data-testid="organiser-identification-number-scroll-id"
              className="w-full focus-visible:outline-none"
            >
              <InputValidationWrapper
                className="flex items-start"
                status={organiserValidation.identificationNumber}
                childRef={
                  errorDetails.firstError === "organiser-identificationNumber"
                    ? firstErrorRef
                    : undefined
                }
              >
                <TextFieldSts
                  label={
                    content?.organiserSection?.identificationSection
                      ?.numberPlaceholder ?? "Identification Number"
                  }
                  data-testid="organiser-identification-number"
                  id="organiser-identification-number"
                  width="100%"
                  name="identificationNumber"
                  maxLength={12}
                  msgCustomStyles={{ width: "auto" }}
                  state={organiserValidation.identificationNumber ?? undefined}
                  message={getIdentificationErrorMessage(
                    organiserErrors.identificationNumber,
                    organiserValidation.identificationNumber
                  )}
                  value={organiserData.identificationNumber}
                  onChange={(value) =>
                    updateOrganiserField("identificationNumber", value)
                  }
                  onBlur={() => {
                    setFieldTouched("organiser", "identificationNumber");

                    // If no travellers, scroll to Continue button
                    if (!hasTravellers && hasMounted.current) {
                      setTimeout(() => {
                        const element = document.getElementById(
                          "required-information-main-cta-button-id"
                        );
                        if (element) {
                          element.scrollIntoView({
                            behavior: "smooth",
                            block: "end",
                          });
                        }
                      }, 200);
                    }
                  }}
                  brand="staysure"
                />
              </InputValidationWrapper>
            </button>
          </div>
        </div>

        {/* Travellers Section - Show only if there are travellers */}
        {hasTravellers && (
          <>
            {/* Separator */}
            <div className="border-t border-border-subdued my-spacing-l"></div>

            {travellersData.map((traveller, index) => {
              const isOptional = isIdentificationOptional(traveller.age);
              const validation = travellersValidation[traveller.encryptedId];
              const errors = travellersErrors[traveller.encryptedId];
              const isLastTraveller = index === travellersData.length - 1;

              // Determine prevFieldRef - if first traveller, use organiser's identification number ref
              // Otherwise, use previous traveller's identification number ref
              const prevFieldRef =
                index === 0
                  ? organiserIdentificationNumberRef
                  : travellerRefs[travellersData[index - 1].encryptedId]
                      ?.identificationNumberRef;

              return (
                <div key={traveller.encryptedId}>
                  <TravellerSection
                    traveller={traveller}
                    validation={validation}
                    errors={errors}
                    isOptional={isOptional}
                    optionalText={content?.translations?.optional ?? ""}
                    content={content}
                    identificationTypes={getFilteredIdentificationTypes(
                      traveller.identificationType,
                      traveller.identificationNumber
                    )}
                    updateTravellerField={updateTravellerField}
                    setFieldTouched={setFieldTouched}
                    errorDetails={errorDetails}
                    firstErrorRef={firstErrorRef}
                    identificationTypeRef={
                      travellerRefs[traveller.encryptedId]
                        ?.identificationTypeRef
                    }
                    identificationNumberRef={
                      travellerRefs[traveller.encryptedId]
                        ?.identificationNumberRef
                    }
                    prevFieldRef={prevFieldRef}
                    hasMounted={hasMounted}
                    isLastTraveller={isLastTraveller}
                  />
                </div>
              );
            })}
          </>
        )}

        {/* InlineInfoBox for error tracking */}
        {showErrors && errorDetails.count > 0 && (
          <div className="mt-spacing-l">
            <InlineInfoBox
              id="required-info-inline-info-box"
              content={
                <p className="text-text-info-1-primary">
                  {getAlertMessageIncompleteSection()}
                  &nbsp;
                  <button
                    type="button"
                    className="underline font-bold leading-[24px]"
                    onClick={() => {
                      const el = firstErrorRef?.current as Element | null;
                      if (el) {
                        const topOffset =
                          el.getBoundingClientRect().top + window.scrollY - 20;
                        window.scrollTo({
                          top: topOffset,
                          behavior: "smooth",
                        });
                      }
                    }}
                  >
                    {getAlertMessageLinkSection()}
                  </button>
                </p>
              }
              style={{
                width: "100%",
                backgroundColor: "#FFF0E9",
                borderColor: "#D06D10",
                padding: "8px 16px 8px 16px",
                borderRadius: "8px",
                color: "#272427",
                fontSize: "16px",
                lineHeight: "24px",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
